import { useTranslations } from '@power-core/toolkit';
import type { FunctionComponent } from 'react';

import { useError } from '@/modules/utils/custom-hooks/use-error';
import { Button } from '@/ui-components/button/button';
import { Flexbox } from '@/ui-components/flexbox/flexbox';
import { Input } from '@/ui-components/input/input';
import { LoadingIndicator } from '@/ui-components/loading-indicator/loading-indicator';

import { useDocumentStatusApi } from '../../hooks/use-document-status-api';
import { ColorPicker } from './color-picker';
import type { NewStatusRowProps } from './types';

export const NewStatusRow: FunctionComponent<NewStatusRowProps> = ({
	projectId,
	newStatus,
	statusState,
	setNewStatus,
	clearStatusState,
}) => {
	const t = useTranslations();
	const { createDocumentStatus } = useDocumentStatusApi();
	const { error: err, isPending } = createDocumentStatus;
	const { error, setError } = useError(err);

	const handleCancelButton = () => {
		clearStatusState();
		setError({ message: null, details: {} });
	};

	const renderActionButtons = () => {
		if (isPending)
			return (
				<Flexbox justify='center' className='w-full'>
					<LoadingIndicator sizing='small' color='primary' />
				</Flexbox>
			);

		if (newStatus?.name && newStatus.color)
			return (
				<Button
					variant='text'
					className='font-normal text-sm w-fit text-accent'
					onClick={() => {
						createDocumentStatus
							.mutateAsync({ documentId: projectId, body: newStatus })
							.then(() => {
								clearStatusState();
							})
							.catch((err) => {
								setError({ message: err.response.data.error.message, details: {} });
							});
					}}
				>
					{t('T_SAVE')}
				</Button>
			);

		return null;
	};

	return (
		!statusState.currentId &&
		statusState.isNew && (
			<Flexbox direction='col' className='pb-5'>
				<hr className='border-t-2 mb-2.5 border-n300_table_background' />
				<Flexbox justify='start' className='mb-2.5 gap-3 p-0.5 pr-1.5 items-baseline'>
					<Input
						value={newStatus?.name}
						name='statusName'
						sizing='small'
						disabled={isPending}
						onChange={(e) => {
							setNewStatus({ ...newStatus, name: e.target.value });
							setError({ message: null, details: {} });
						}}
						containerClassname='w-[220px]'
						error={t(error.message ?? '')}
						placeholder={t('T_ENTER_STATUS_NAME')}
					/>
					<ColorPicker
						label={t('T_SELECT_COLOR')}
						color={newStatus?.color}
						onColorChange={(color) => setNewStatus({ ...newStatus, color: color })}
					/>
					<Flexbox className={`gap-1 grow ${newStatus?.name && newStatus.color && !isPending ? 'justify-start' : 'justify-end mr-4'}`}>
						{renderActionButtons()}
						<Button
							variant='text'
							className={`text-dark font-normal text-sm w-fit hover:text-brand  dark:text-n700_text_dark_mode ${isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer opacity-100'}`}
							onClick={isPending ? () => {} : handleCancelButton}
						>
							{t('T_CANCEL')}
						</Button>
					</Flexbox>
				</Flexbox>
			</Flexbox>
		)
	);
};
